import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Lock, CheckCircle2, ChevronDown, Loader2 } from "lucide-react";
import { API, STATUSES, PHONE_DISPLAY } from "./data";

const field =
  "w-full rounded-xl border border-[#D9D3C7] bg-white px-4 py-3.5 text-base text-[var(--ink)] placeholder:text-[#7A8796] outline-none transition-[border-color,box-shadow] focus:border-[var(--navy)] focus:ring-4 focus:ring-[var(--navy)]/10";

const Label = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-semibold text-[var(--navy)]">{children}</label>
);

export const LeadForm = ({ prefix = "lead", compact = false }) => {
  const [form, setForm] = useState({ name: "", phone: "", status: "", details: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.status) {
      toast.error("Kérjük, adja meg a nevet (vagy álnevet), a telefonszámot és az ingatlan állapotát.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source: prefix });
      setDone(true);
      toast.success("Köszönjük! 1 órán belül visszahívjuk a részletekkel.");
    } catch (err) {
      const msg = err?.response?.status === 422 ? "Kérjük, ellenőrizze a telefonszámot." : `Hiba történt. Kérjük, hívjon minket: ${PHONE_DISPLAY}`;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div
        data-testid={`${prefix}-success`}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-start gap-3 rounded-2xl bg-[#EEF5F0] p-6"
      >
        <CheckCircle2 className="h-9 w-9 text-[#2F7A4A]" />
        <p className="font-display text-xl font-extrabold text-[var(--navy)]">Megkaptuk az üzenetét.</p>
        <p className="text-[var(--muted)]">1 órán belül visszahívjuk a részletekkel. Sürgős esetben hívjon most: <a className="font-bold text-[var(--navy)] underline" href="tel:+36205421428">{PHONE_DISPLAY}</a></p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`${prefix}-form`} className="space-y-4" noValidate>
      <div className={compact ? "grid gap-4" : "grid gap-4 sm:grid-cols-2"}>
        <div>
          <Label htmlFor={`${prefix}-name`}>Név vagy Álnév</Label>
          <input id={`${prefix}-name`} data-testid={`${prefix}-name-input`} className={field} placeholder="pl. Kovács vagy „Anna”" value={form.name} onChange={set("name")} autoComplete="name" maxLength={80} />
        </div>
        <div>
          <Label htmlFor={`${prefix}-phone`}>Telefonszám</Label>
          <input id={`${prefix}-phone`} data-testid={`${prefix}-phone-input`} className={field} placeholder="06 20 123 4567" value={form.phone} onChange={set("phone")} type="tel" inputMode="tel" autoComplete="tel" maxLength={25} />
        </div>
      </div>
      <div>
        <Label htmlFor={`${prefix}-status`}>Ingatlan állapota</Label>
        <div className="relative">
          <select id={`${prefix}-status`} data-testid={`${prefix}-status-select`} className={`${field} appearance-none pr-10 ${form.status ? "" : "text-[#7A8796]"}`} value={form.status} onChange={set("status")}>
            <option value="" disabled>Válasszon…</option>
            {STATUSES.map((s) => <option key={s} value={s} className="text-[var(--ink)]">{s}</option>)}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-[var(--navy)]" />
        </div>
      </div>
      {!compact && (
        <div>
          <Label htmlFor={`${prefix}-details`}>Részletek / Üzenet <span className="font-normal text-[var(--muted)]">(Opcionális)</span></Label>
          <textarea id={`${prefix}-details`} data-testid={`${prefix}-details-input`} rows={4} className={`${field} resize-none`} placeholder="Település, alapterület, sürgősség – amit fontosnak tart." value={form.details} onChange={set("details")} maxLength={2000} />
        </div>
      )}
      <motion.button
        whileHover={{ scale: 1.015 }}
        whileTap={{ scale: 0.985 }}
        type="submit"
        disabled={loading}
        data-testid={`${prefix}-submit-button`}
        className="group flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--cta)] px-6 py-4 text-lg font-bold text-white shadow-[0_14px_30px_-10px_rgba(220,68,32,0.75)] transition-colors hover:bg-[var(--cta-hover)] disabled:opacity-70"
      >
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
        {compact ? "Díjmentes Ajánlatkérés" : "Ajánlat Kérése"}
        {!loading && <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />}
      </motion.button>
      <p className="flex items-center gap-2 text-sm text-[var(--muted)]">
        <Lock className="h-4 w-4 shrink-0 text-[var(--navy)]" /> Bizalmasan kezeljük. Álnévvel is kitölthető.
      </p>
    </form>
  );
};
